import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { apiFetch } from '../api';

const SystemCheckDialog = ({ open, onProceed, videoRef, audioAnalyser, onVideoReady, onAudioReady, faceCheckError, bypassAudioReady }) => {
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);
  const [latency, setLatency] = useState(null);
  const [bandwidth, setBandwidth] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const [audioReady, setAudioReady] = useState(bypassAudioReady ? true : false);
  const [readyTimeout, setReadyTimeout] = useState(false);

  // Handlers to be passed to video/audio setup
  const handleVideoReady = () => setVideoReady(true);
  const handleAudioReady = () => {
    if (!bypassAudioReady) setAudioReady(true);
  };

  useEffect(() => {
    if (onVideoReady) onVideoReady(handleVideoReady);
    if (onAudioReady) onAudioReady(handleAudioReady);
    // eslint-disable-next-line
  }, []);

  // Timeout for video/audio readiness
  useEffect(() => {
    if (!open) return;
    setReadyTimeout(false);
    const timeout = setTimeout(() => {
      if (!videoReady || (!audioReady && !bypassAudioReady)) setReadyTimeout(true);
    }, 8000);
    return () => clearTimeout(timeout);
  }, [open, videoReady, audioReady, bypassAudioReady]);

  // Lighting check
  const checkLighting = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      console.log('Lighting: video not ready');
      return true; // Don't fail if not ready
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    if (canvas.width === 0 || canvas.height === 0) {
      console.log('Lighting: canvas has zero dimension');
      return true;
    }
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    let brightness = 0;
    for (let i = 0; i < data.length; i += 4) {
      brightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
    }
    brightness /= (data.length / 4);
    console.log('Lighting brightness:', brightness);
    if (brightness < 5) return true;
    return brightness > 50;
  };

  // Multiple faces check
  const checkMultipleFaces = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      console.log('Multiple faces: video not ready');
      return true; // Don't fail if not ready
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    if (canvas.width === 0 || canvas.height === 0) {
      console.log('Multiple faces: canvas has zero dimension');
      return true;
    }
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg');
    let attempts = 0;
    while (attempts < 3) {
      try {
        const res = await apiFetch('/api/detect-faces', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: dataUrl })
        });
        const data = await res.json();
        if (!data.success) throw new Error('Face detection failed');
        console.log('Multiple faces detected:', data.faceDetails.length);
        return data.faceDetails.length === 1;
      } catch (err) {
        console.log('Multiple faces error:', err.message);
        attempts++;
        await new Promise(res => setTimeout(res, 500));
      }
    }
    // If detection fails, do not fail the check
    return true;
  };

  // Webcam functional check
  const checkWebcam = () => {
    const video = videoRef.current;
    const ready =
      !!video &&
      video.readyState >= 2 &&
      !video.paused &&
      !video.ended &&
      video.videoWidth > 0 &&
      video.videoHeight > 0;
    console.log('[SystemCheckDialog] Webcam functional check:', {
      ready,
      readyState: video?.readyState,
      paused: video?.paused,
      ended: video?.ended,
      width: video?.videoWidth,
      height: video?.videoHeight,
      srcObject: video?.srcObject
    });
    return ready;
  };

  // Noise detection
  const checkNoise = () => {
    if (!audioAnalyser.current) {
      console.log('Noise: audioAnalyser not ready');
      return true; // Pass if not ready
    }
    const bufferLength = audioAnalyser.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    audioAnalyser.current.getByteFrequencyData(dataArray);
    const average = dataArray.reduce((a, b) => a + b) / bufferLength;
    console.log('Noise average:', average);
    return average < 40;
  };

  // Internet quality
  const checkInternet = async () => {
    try {
      const ping = await apiFetch('/api/ping');
      if (!ping.ok) throw new Error('Ping failed');
      const latency = Math.floor(Math.random() * 100) + 50; // Simulate latency
      return { online: true, latency, bandwidth: 1 };
    } catch (err) {
      console.log('Internet check error:', err.message);
      return { online: false, latency: null, bandwidth: null };
    }
  };

  const runChecks = async () => {
    setChecking(true);
    setResults(null);
    setLatency(null);
    setBandwidth(null);
    setReadyTimeout(false);
    // Wait for video and audio to be ready
    let tries = 0;
    while (tries < 40 && (!videoReady || (!audioReady && !bypassAudioReady))) {
      await new Promise(res => setTimeout(res, 250));
      tries++;
    }
    if (!videoReady || (!audioReady && !bypassAudioReady)) {
      setReadyTimeout(true);
      setChecking(false);
      return;
    }
    const lighting = checkLighting();
    const multipleFaces = await checkMultipleFaces();
    const webcam = checkWebcam();
    const noise = checkNoise();
    const internet = await checkInternet();
    const checkResults = { lighting, multipleFaces, webcam, noise, internet };
    setResults(checkResults);
    setChecking(false);
    try {
      await apiFetch('/api/log-system-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkResults),
      });
    } catch (err) {
      // Intentionally left empty: logging system check failures is non-critical
    }
  };

  useEffect(() => {
    if (open && videoReady && (audioReady || bypassAudioReady)) runChecks();
    // eslint-disable-next-line
  }, [open, videoReady, audioReady, bypassAudioReady]);

  // Only require webcam and face (multipleFaces) to pass
  const allPass = results && results.webcam && results.multipleFaces;

  // List failed checks for summary
  const getFailedChecks = () => {
    if (!results) return [];
    const failed = [];
    if (!results.webcam) failed.push('Webcam not working');
    if (!results.multipleFaces) failed.push('No face or multiple faces detected');
    if (!results.lighting) failed.push('Lighting is too low');
    if (!results.noise) failed.push('High background noise');
    if (!results.internet?.online) failed.push('Internet offline');
    else {
      if (results.internet.latency >= 300) failed.push('High latency');
      if (results.internet.bandwidth <= 0.5) failed.push('Low bandwidth');
    }
    return failed;
  };

  const renderResult = (pass, label, detail, required = false) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      {pass ? <CheckCircleIcon color="success" sx={{ mr: 1 }} /> : <ErrorIcon color={required ? 'error' : 'warning'} sx={{ mr: 1 }} />}
      <Typography>{label}{detail ? `: ${detail}` : ''}{required && ' (Required)'}</Typography>
    </Box>
  );

  const failedChecks = getFailedChecks();

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>System Check</DialogTitle>
      <DialogContent>
        {checking || !results ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Running system checks...</Typography>
          </Box>
        ) : (
          <Box>
            {renderResult(results.lighting, 'Lighting', results.lighting ? 'Good' : 'Too Dark', false)}
            {renderResult(results.multipleFaces, 'Face Detected', results.multipleFaces ? 'One face detected' : 'Multiple/No face detected', true)}
            {renderResult(results.webcam, 'Webcam', results.webcam ? 'Functional' : 'Not working', true)}
            {renderResult(results.noise, 'Noise', results.noise ? 'Low' : 'High background noise detected', false)}
            {renderResult(results.internet.online, 'Internet', results.internet.online ? 'Online' : 'Offline', false)}
            {results.internet.online && (
              <>
                {renderResult(results.internet.latency < 300, 'Latency', `${results.internet.latency} ms ${results.internet.latency < 300 ? '(Good)' : '(High)'}`, false)}
                {renderResult(results.internet.bandwidth > 0.5, 'Bandwidth', `${results.internet.bandwidth?.toFixed(2)} MB/s ${results.internet.bandwidth > 0.5 ? '(Good)' : '(Low)'}`, false)}
              </>
            )}
            {/* Summary of failed checks */}
            {failedChecks.length > 0 && (
              <Box sx={{ mt: 2, color: 'error.main' }}>
                <Typography variant="subtitle1">Issues detected:</Typography>
                <ul>
                  {failedChecks.map((fail, i) => <li key={i}>{fail}</li>)}
                </ul>
              </Box>
            )}
            {/* Face verification error from parent */}
            {faceCheckError && (
              <Box sx={{ mt: 2, color: 'error.main' }}>
                <Typography variant="subtitle1">{faceCheckError}</Typography>
              </Box>
            )}
            {/* Video/audio readiness timeout */}
            {readyTimeout && (
              <Box sx={{ mt: 2, color: 'error.main' }}>
                <Typography variant="subtitle1">Webcam or microphone not ready. Please check permissions and device connection.</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={runChecks} disabled={checking}>Retry</Button>
        <Button onClick={onProceed} disabled={!allPass || checking || readyTimeout} variant="contained" color="primary">Proceed</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemCheckDialog; 